import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  SearchIcon,
} from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash/fp';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import {
  NAVBAR_COLLAPSED_WIDTH,
  NAVBAR_EXPANDED_WIDTH,
} from 'dashboard/components/Navbar/constants';
import EditModeNavbarItem from 'dashboard/components/Navbar/EditModeNavbarItem';
import EnvironmentCreateModal from 'dashboard/components/Navbar/EnvironmentCreateModal';
import EnvironmentPickerDropdown from 'dashboard/components/Navbar/EnvironmentPickerDropdown';
import Footer from 'dashboard/components/Navbar/Footer';
import NavbarItem from 'dashboard/components/Navbar/NavbarItem';
import OmnisearchModal from 'dashboard/components/Navbar/OmnisearchModal';
import OmnisearchTargetInput from 'dashboard/components/Navbar/OmnisearchTargetInput';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { printMixins } from 'dashboard/lib/printMixins';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Environment, EnvironmentType } from 'dashboard/models/environment';
import { DashboardNavbarSetting, ProductFeatureName } from 'dashboard/models/organization';
import { NavItem } from 'dashboard/models/role';
import { User } from 'dashboard/models/user';
import { saveCurrentUser } from 'dashboard/redux/actions/current-user';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { setEnvironments } from 'dashboard/redux/actions/organization';
import { RootState } from 'dashboard/redux/store';
import { setAndPersistCurrentEnvironment } from 'dashboard/redux/thunk-actions/current-environment';
import { expireSession } from 'dashboard/redux/thunk-actions/current-user-session';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { Hairline } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';
import { SegmentClient } from 'lib/clients/SegmentClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useSidebarConfig from 'lib/hooks/useSidebarConfig';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

interface NavbarProps {
  isCollapsed: boolean;
  isEditing: boolean;
  toggleCollapse: () => void;
  toggleEdit: () => void;
  openSupportChat: (initialInputText?: string) => void;
}

const Navbar = ({
  currentEnvironment,
  currentUser,
  organization,
  environments,
  readmeJwtToken,
  impersonationMode,
  setAndPersistCurrentEnvironment,
  setEnvironments,
  expireSession,
  isCollapsed,
  isEditing,
  toggleCollapse,
  toggleEdit,
  openSupportChat,
  showMessage,
  clearMessages,
  saveCurrentUser,
}: NavbarProps & PropsFromRedux) => {
  const hasRoleBasedNavbarSetting =
    organization.attributes['dashboard-navbar-setting'] === DashboardNavbarSetting.RoleBased;
  const [userHiddenIds, setUserHiddenIds] = useState<NavItem[]>([]);
  const existingUserHiddenIds = currentUser.attributes['hidden-nav-items'];

  useEffect(() => {
    setUserHiddenIds(existingUserHiddenIds || []);
  }, [existingUserHiddenIds]);

  const isDirty = useMemo(() => {
    return !isEqual(existingUserHiddenIds, userHiddenIds);
  }, [existingUserHiddenIds, userHiddenIds]);

  const sections = useSidebarConfig({ readmeJwtToken });

  const canCreateAsMultiEnv = currentUser.attributes['is-admin'];

  const canCreateProduction =
    canCreateAsMultiEnv &&
    !productFeatureDisabled(
      organization,
      ProductFeatureName.EnvironmentsCreate,
      ApiModel.EnvironmentProduction,
    );

  const canCreateSandbox =
    canCreateAsMultiEnv &&
    !productFeatureDisabled(
      organization,
      ProductFeatureName.EnvironmentsCreate,
      ApiModel.EnvironmentSandbox,
    );

  const [
    showEnvironmentCreate,
    { enable: openEnvironmentCreate, disable: closeEnvironmentCreate },
  ] = useToggle();

  const {
    error: errorEnvironmentCreate,
    callback: handleEnvironmentCreate,
    loading: submittingEnvironmentCreate,
  } = useApiCallback(
    async (type: string, name: string) => {
      const newEnvironment = (
        await SessionClientDashboard.postResourceCreate<Environment>({
          type: type as EnvironmentType,
          attributes: { name },
        })
      ).data;

      setEnvironments([...environments, newEnvironment]);
      closeEnvironmentCreate();
      showMessage('Successfully created environment', MessageType.Success);
    },
    [closeEnvironmentCreate, showMessage, setEnvironments, environments],
  );

  useApiErrorHandler({
    error: errorEnvironmentCreate,
    onError: () => {
      showMessage('Failed to create environment', MessageType.Failure);
    },
    onNetworkError: () =>
      showMessage('Something went wrong, please try again', MessageType.Failure),
  });

  const {
    error: errorUpdateUser,
    callback: updateUserNavbar,
    loading: submittingUpdateUser,
  } = useApiCallback(async () => {
    clearMessages();

    const response = await SessionClientDashboard.updateResourceSingleton<User>({
      type: ApiModel.CurrentUser as const,
      attributes: { 'hidden-nav-items': userHiddenIds },
    });

    saveCurrentUser(response.data);

    showMessage(
      'Successfully updated navbar items for all of your environments',
      MessageType.Success,
    );
  }, [showMessage, userHiddenIds]);

  useApiErrorHandler({
    error: errorUpdateUser,
    onError: () => {
      showMessage('Failed to update navbar items', MessageType.Failure);
    },
    onNetworkError: () =>
      showMessage('Something went wrong, please try again', MessageType.Failure),
  });

  const [showOmnisearchModal, { enable: openOmnisearchModal, disable: closeOmnisearchModal }] =
    useToggle();

  const handleOmnisearchShortcutKeyPress = (event: KeyboardEvent) => {
    // Check Command + K (macOS) or Ctrl + K (Windows and Linux)
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      openOmnisearchModal();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleOmnisearchShortcutKeyPress);
    return () => {
      window.removeEventListener('keydown', handleOmnisearchShortcutKeyPress);
    };
  }, []);

  return (
    <NavbarContainer
      id='navbar__container'
      data-test='navbar__container'
      isCollapsed={isCollapsed}
      isSandbox={currentEnvironment && currentEnvironment.type === ApiModel.EnvironmentSandbox}
      role='navigation'
    >
      <ModalWrapper
        shouldCloseOnEsc={true}
        isOpen={showEnvironmentCreate}
        handleCloseModal={closeEnvironmentCreate}
      >
        <EnvironmentCreateModal
          canCreateProduction={!!canCreateProduction}
          canCreateSandbox={!!canCreateSandbox}
          submitting={submittingEnvironmentCreate}
          handleCreate={handleEnvironmentCreate}
          handleClose={closeEnvironmentCreate}
        />
      </ModalWrapper>
      <ModalWrapper
        shouldCloseOnEsc={true}
        isOpen={showOmnisearchModal}
        handleCloseModal={closeOmnisearchModal}
      >
        <OmnisearchModal closeModal={closeOmnisearchModal} openSupportChat={openSupportChat} />
      </ModalWrapper>
      <Header>
        <EnvironmentPickerDropdown
          currentEnvironment={currentEnvironment}
          environments={environments}
          canCreateEnvironments={!!canCreateProduction || !!canCreateSandbox}
          handleCreateNewEnvironment={openEnvironmentCreate}
          handleSwitchEnvironment={(environment) => {
            SegmentClient.sendSegmentTrackEvent('Environment Changed', {
              organizationId: organization.id,
              oldEnvironmentId: currentEnvironment.id,
              oldEnvironmentType: currentEnvironment.type,
              newEnvironmentId: environment.id,
              newEnvironmentType: environment.type,
              impersonationMode: impersonationMode,
            });

            showMessage(`Now viewing ${environment.attributes.name}`, MessageType.Notice);
            setAndPersistCurrentEnvironment(environment);
          }}
        />

        {!isEditing && (
          <CollapseIcon
            id='navbar__collapse_button'
            data-test='navbar__collapse'
            onClick={toggleCollapse}
          >
            {isCollapsed ? <ChevronDoubleRightIcon /> : <ChevronDoubleLeftIcon />}
          </CollapseIcon>
        )}
      </Header>
      <OmnisearchWrapper isCollapsed={isCollapsed}>
        {isCollapsed ? (
          <>
            <CollapsedBorder height={1} />
            <Spacer height={1} />
            <OmnisearchIcon>
              <SearchIcon onClick={openOmnisearchModal} />
            </OmnisearchIcon>
          </>
        ) : (
          <OmnisearchTargetInput onFocus={openOmnisearchModal} />
        )}
      </OmnisearchWrapper>
      {isEditing ? (
        <>
          <SectionsScrollable isCollapsed={isCollapsed}>
            {sections.map((section) => {
              if (section.label === 'HOME') {
                return null;
              }

              const sectionItems = section.items.map((item) => {
                const visibleLinks = item.links.filter((link) => !link.hidden);

                if (item.hidden || visibleLinks.length === 0) {
                  return null;
                }

                return (
                  <EditModeNavbarItem
                    key={item.id}
                    item={item}
                    links={visibleLinks}
                    userHiddenIds={userHiddenIds}
                    setUserHiddenIds={setUserHiddenIds}
                  />
                );
              });

              if (!sectionItems.some(Boolean)) {
                return null;
              }

              return (
                <Fragment key={section.id}>
                  <SectionTitle isCollapsed={isCollapsed} label={section.label} />
                  {sectionItems}
                </Fragment>
              );
            })}
          </SectionsScrollable>
          <StyledEditModeButtonContainer>
            <Button
              id='navbar-edit__cancel-button'
              variant='basic'
              isPending={submittingUpdateUser}
              onClick={() => {
                toggleEdit();
                setUserHiddenIds(existingUserHiddenIds || []);
              }}
            >
              Cancel
            </Button>
            <Spacer width={1} height={0} />
            <Button
              id='navbar-edit__submit-button'
              type='submit'
              disabled={!isDirty}
              isPending={submittingUpdateUser}
              onClick={() => {
                updateUserNavbar();
                toggleEdit();
              }}
            >
              Save changes
            </Button>
          </StyledEditModeButtonContainer>
        </>
      ) : (
        <SectionsScrollable isCollapsed={isCollapsed}>
          {sections.map((section) => {
            const sectionItems = section.items.map((item) => {
              if (
                item.hidden ||
                (!hasRoleBasedNavbarSetting &&
                  currentUser?.attributes?.['hidden-nav-items']?.includes(item.navItemRedesign))
              ) {
                return null;
              }

              const visibleLinks = item.links.filter(
                (link) =>
                  !link.hidden &&
                  (currentUser?.attributes['is-admin'] ||
                    (hasRoleBasedNavbarSetting
                      ? currentUser?.attributes?.['enabled-nav-items']?.includes(link.navItem)
                      : !currentUser?.attributes?.['hidden-nav-items']?.includes(
                          link.navItemRedesign,
                        ))),
              );

              if (visibleLinks.length === 0) {
                return null;
              }

              return (
                <NavbarItem
                  key={item.id}
                  isCollapsed={isCollapsed}
                  item={item}
                  visibleLinks={visibleLinks}
                />
              );
            });

            if (!sectionItems.some(Boolean)) {
              return null;
            }

            return (
              <Fragment key={section.id}>
                <SectionTitle isCollapsed={isCollapsed} label={section.label} />
                {sectionItems}
              </Fragment>
            );
          })}
        </SectionsScrollable>
      )}
      <Footer
        isCollapsed={isCollapsed}
        currentUser={currentUser}
        organization={organization}
        impersonationMode={impersonationMode}
        expireSession={expireSession}
        toggleNavbarCollapse={toggleCollapse}
        toggleNavbarEdit={toggleEdit}
      />
    </NavbarContainer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    impersonationMode: state.currentUser.impersonationMode,
    currentUser: state.currentUser.currentUser,
    readmeJwtToken: state.currentUser.readmeJwtToken,
    environments: state.organization.environments,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  saveCurrentUser,
  setAndPersistCurrentEnvironment,
  setEnvironments,
  clearMessages,
  showMessage,
  expireSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Navbar);

const CollapseIcon = styled.div`
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 0px;
  transform: translate(50%, -50%);
  // needed to be interactive when LoadingModal is displayed
  z-index: ${Z_INDICES.modalElevated};

  border: 1px solid ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
  background-color: ${Color.BackgroundLabelWhite};
  padding-top: ${Spacing[2]};
  padding-bottom: ${Spacing[2]};

  display: flex;
  align-items: center;

  path {
    fill: ${Color.BorderIconSecondary};
  }
  // NB: this is overridden by &:hover in the container
  opacity: 0;
`;

const NavbarContainer = styled('nav')<{ isCollapsed: boolean; isSandbox: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: ${Z_INDICES.sideNav};
  height: 100vh;
  width: ${({ isCollapsed }) => (isCollapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_EXPANDED_WIDTH)}px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.color.background.primary};
  box-shadow: ${Hairline.Right};
  overflow: visible;
  border-right: ${({ isSandbox }) => isSandbox && `1px solid ${Color.CoreOrange600}`};

  transition-duration: ${({ isCollapsed }) =>
    isCollapsed ? TransitionDuration.Medium : TransitionDuration.Fast};

  &:hover ${CollapseIcon} {
    opacity: 1;
  }

  ${printMixins.hide}
`;

const Header = styled.div`
  position: relative;
  padding: ${Spacing[2]};
`;

const OmnisearchWrapper = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${({ isCollapsed }) =>
    isCollapsed ? `${Spacing[0]} ${Spacing[4]}` : `${Spacing[1]} ${Spacing[4]} ${Spacing[0]}`};
  transition: 0.5s;
`;

const OmnisearchIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: ${Spacing[2]} ${Spacing[2]};
  cursor: pointer;

  svg {
    color: ${Color.BorderIconSecondary};
  }

  :hover {
    background-color: ${Color.CorePurple100};

    svg {
      color: ${Color.BorderIconActive};
    }
  }
`;

const SectionsScrollable = styled.div<{ isCollapsed: boolean }>`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  margin: ${({ isCollapsed }) =>
    isCollapsed ? `${Spacing[0]} ${Spacing[4]} ${Spacing[2]}` : `${Spacing[2]} ${Spacing[3]}`};
`;

interface SectionTitleProps {
  isCollapsed: boolean;
  label: string;
}

export const SectionTitle = ({ isCollapsed, label }: SectionTitleProps) => (
  <SectionHeader key={label}>
    <Spacer width={1} height={0} />

    <CollapsedContainer isCollapsed={isCollapsed}>
      <CollapsedBorder height={1} />
      <Spacer height={1} />
    </CollapsedContainer>

    <SectionParagraph isCollapsed={isCollapsed} color='secondary' size='xs'>
      {label}
    </SectionParagraph>

    <Spacer width={1} height={0} />
  </SectionHeader>
);

const SectionHeader = styled.div`
  position: relative;
  display: flex;
  padding-top: ${Spacing[2]};
  padding-bottom: ${Spacing[2]};
  user-select: none;
`;

const SectionParagraph = styled(Paragraph)<{ isCollapsed: boolean }>`
  font-size: 10px;
  letter-spacing: 1.25pt;

  opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
  transition: 0.5s;
`;

const CollapsedContainer = styled.div<{ isCollapsed: boolean }>`
  position: absolute;

  display: flex;
  flex-direction: column;
  width: 100%;

  opacity: ${({ isCollapsed }) => (isCollapsed ? 1 : 0)};
  transition: 0.5s;
`;

const CollapsedBorder = styled(Spacer)`
  border-bottom: 1px solid ${Color.BorderHairline};
  width: 100%;
`;

const StyledEditModeButtonContainer = styled.div`
  padding: 10px;
  display: flex;
`;
